/**
 * WHAT STANDS BEHIND A SETTINGS SCREEN (SH-A8 round 3, owner-48 and owner-56).
 *
 * Round 1 gave the product `useBack()` - the history stack first, then the tab's memory of
 * the last app path, then a stated parent - and put it under every pane's own back link. The
 * toolbar's LEADING capsule was left out, and it is the control Apple's rule is actually
 * about: "Elements that let people return to the previous document and show or hide a sidebar
 * appear at the far leading edge" and "People know that the standard Back button lets them
 * retrace their steps through a hierarchy of information"
 * (https://developer.apple.com/design/human-interface-guidelines/toolbars). It read "Back to
 * the app" on every settings screen and left for the app from a pushed pane, which is why
 * each pane had to draw a second back affordance in its body (owner-56: a back above the
 * page) and why the owner's own words are "der Backbutton sollte mich immer zu meinem
 * vorherigen Ort bringen".
 *
 * THE CONTROL NEEDS TWO THINGS THE HOOK CANNOT KNOW: what to call itself, and where to go
 * when there is nothing to pop. Apple's back button carries the PREVIOUS SCREEN'S TITLE, and
 * a deep link SEEDS the same path rather than inventing a parent ("Use a path to support deep
 * links, state restoration, or other kinds of programmatic navigation",
 * https://developer.apple.com/documentation/swiftui/navigationstack). Both answers are the
 * settings hierarchy, so the hierarchy is written down once, here, and the shell reads it -
 * instead of a route-specific target sitting in the toolbar.
 */

import { FULFILLMENT } from '../fulfillment/shared/configuration';

/**
 * THE PANES AND WHAT EACH ONE IS CALLED. This is the settings sidebar's own list of rows
 * (`app/shell/SettingsNavigation.tsx` renders its labels from here), and a pane's row label
 * is also its window title, so one table answers both questions and the back control can
 * never name a screen something the screen does not call itself.
 *
 * SENTENCE CASE AND THE DECIDED WORDS SINCE 2026-10-04 (redesign register A5-5: "Row names as on
 * the variants page (Users, AI assistant from the words list)"; O4): Members is Users, Athena is
 * AI assistant, and the pane title reads the same (coordinator decision Q-NAV-6).
 *
 * NO APPEARANCE PANE SINCE 2026-10-05 (register A8-7): its settings are submenus of the account
 * menu's Appearance section (`app/shell/AccountMenu.tsx`), and `/settings/appearance` redirects.
 */
export const SETTINGS_PANES = {
  /** The person's own pane (fulfillment P2 item 3): which objects their sidebar shows. */
  sidebar: 'Sidebar',
  objects: 'Objects and fields',
  members: 'Users',
  identity: 'Identity',
  workflows: 'Workflows',
  webhooks: 'Webhooks',
  inbound: 'Inbound sources',
  agent: 'AI assistant',
  imports: 'Imports',
  exports: 'Exports',
  health: 'Health',
  /**
   * THE COACHING BOT'S PANE (register A7-S4, A8-3), titled by the configuration because the title
   * is a product name (DESIGN.md priority 7, D16). Its first section is the onboarding's welcome
   * texts, one per package and language (fulfillment P2 item 5), which had a pane of their own.
   */
  bot: FULFILLMENT.botSettingsTitle,
  instance: 'Instance',
} as const;

export type SettingsPaneSlug = keyof typeof SETTINGS_PANES;

/** The sections of the bot's pane, by the words they had before the pane held them (A8-3). */
export const BOT_PANE_SECTIONS = {
  welcomeMessages: 'Welcome messages',
} as const;

/**
 * WHAT THE SETTINGS SEARCH MATCHES BESIDE A PANE'S TITLE: the titles of its sections, so a person
 * who looks for a section by the name it had as a pane of its own still finds the pane that holds
 * it ("welcome" finds the bot's pane). Only existing and configured words; a pane without
 * sections has no entry.
 */
export const SETTINGS_PANE_SEARCH_WORDS: Partial<Record<SettingsPaneSlug, readonly string[]>> = {
  bot: Object.values(BOT_PANE_SECTIONS),
};

/**
 * SECTIONS OF A PANE, NOT SCREENS PUSHED ON TOP OF IT. `/settings/members/roles` renders the
 * Members pane with its second section chosen (`App.tsx`, `<Route path="settings/members/roles"
 * element={<MembersPage tab="roles" />} />`), so it is the same screen as the pane's root and
 * its back control says the same thing; the role DETAIL one segment deeper is the push.
 */
const PANE_SECTIONS: Partial<Record<SettingsPaneSlug, readonly string[]>> = {
  members: ['roles'],
};

/**
 * `/settings` DRAWS NO SCREEN OF ITS OWN: `App.tsx` redirects it to the first pane
 * (`<Route path="settings" element={<Navigate to="/settings/objects" replace />} />`), so that
 * pane IS the settings root - the screen behind every other pane, and the one screen in the
 * area whose own back control leaves settings altogether.
 */
export const SETTINGS_ROOT_PATH = '/settings';
const SETTINGS_ROOT_PANE: SettingsPaneSlug = 'objects';

/** The app has no one-word name on this chrome, so the root control keeps the words it had. */
export const BACK_TO_THE_APP = 'Back to the app';

export interface SettingsPrevious {
  /**
   * WHERE THE PERSON IS STANDING, which the shell needs because the phone's settings list is
   * the "More" sheet and not a route (SH-3): below the breakpoint everything but a pushed
   * pane goes back to that sheet.
   */
  readonly screen: 'root' | 'pane' | 'pushed';
  /** The screen behind this one, or `null` for "wherever in the app the person came from". */
  readonly to: string | null;
  /** What the control calls itself: the previous screen's title. */
  readonly label: string;
}

/**
 * The screen behind `pathname`, or `null` when the path is not a settings screen at all.
 */
export function settingsPrevious(pathname: string): SettingsPrevious | null {
  const segments = pathname.split('/').filter((segment) => segment.length > 0);
  if (segments[0] !== 'settings') return null;
  const slug = segments[1];
  /**
   * `/settings` itself and anything under an unknown pane (a mistyped link, a pane behind a
   * permission the person does not hold) are answered as the root: the app is the only place
   * behind them that certainly exists.
   */
  if (slug === undefined || !(slug in SETTINGS_PANES))
    return { screen: 'root', to: null, label: BACK_TO_THE_APP };
  const pane = slug as SettingsPaneSlug;
  const rest = segments.slice(2);
  const section = rest[0];
  const onPane =
    rest.length === 0 ||
    (rest.length === 1 && section !== undefined && (PANE_SECTIONS[pane] ?? []).includes(section));
  if (onPane) {
    return pane === SETTINGS_ROOT_PANE && rest.length === 0
      ? { screen: 'root', to: null, label: BACK_TO_THE_APP }
      : { screen: 'pane', to: SETTINGS_ROOT_PATH, label: 'Settings' };
  }
  return {
    screen: 'pushed',
    to: `/${segments.slice(0, -1).join('/')}`,
    label: SETTINGS_PANES[pane],
  };
}
